import { useEffect, useState } from "react"
import Dialog from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Textarea from "@/v2-ui/textarea"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import { errorToast } from "@/v2-ui/toast"
import Button, { TooltipButton } from "@/v2-backoffice-client/components/button"
import { useUptimeSetMuteMutation } from "@/v2-backoffice-client/uptime/mute/setMute/uptime.setMute.mutation"
import { useUptimeClearMuteMutation } from "@/v2-backoffice-client/uptime/mute/clearMute/uptime.clearMute.mutation"
import {
  formatRelativeTime,
  getCategoryLabel,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"

export type UptimeBulkMuteActionProps = {
  selectedRows: UptimeTargetFragment[]
  onComplete?: () => void
}

// Bulk mute / unmute UI for the Table.SelectionToolbar.
//
// Behaviour mirrors the per-row UptimeMuteButton: muting opens a
// dialog so the operator can attach a single shared reason to the
// whole selection ("scheduled maintenance until Tuesday"), while
// unmuting fires inline because it's destructive-by-omission rather
// than destructive (you're just resuming alerts).
//
// We deliberately fan the mutation out client-side instead of adding
// a `setUptimeTargetMutes(...)` GraphQL endpoint -- selections rarely
// exceed a few dozen rows, the mutation is fast, and Apollo's
// normalised cache absorbs each per-target response independently so
// the UI updates progressively while the batch runs.
function UptimeBulkMuteAction(props: Readonly<UptimeBulkMuteActionProps>) {
  const { selectedRows, onComplete } = props
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const [setMute, { error: muteError, reset: resetMute }] =
    useUptimeSetMuteMutation()
  const [clearMute, { loading: clearing, error: clearError }] =
    useUptimeClearMuteMutation()

  errorToast(muteError ?? null, { title: "Failed to mute targets" })
  errorToast(clearError ?? null, { title: "Failed to unmute targets" })

  // Re-prime the dialog every time it opens so leftover text from
  // an earlier selection doesn't bleed into the next batch.
  useEffect(() => {
    if (isDialogOpen) {
      setReason("")
      setProgress({ done: 0, total: selectedRows.length })
      resetMute()
    }
  }, [isDialogOpen, selectedRows.length, resetMute])

  const allMuted =
    selectedRows.length > 0 && selectedRows.every((t) => t.IsMuted)
  const allActive =
    selectedRows.length > 0 && selectedRows.every((t) => !t.IsMuted)
  const isMixed = !allMuted && !allActive

  // Only the Mute button has an in-flight indicator (it shows a
  // dialog with progress); Unmute uses the mutation's own loading.
  const isMuting = progress.total > 0 && progress.done < progress.total

  async function runMuteBatch() {
    const targets = selectedRows
    setProgress({ done: 0, total: targets.length })

    let done = 0
    for (const target of targets) {
      try {
        await setMute({
          variables: {
            input: {
              source: target.Source,
              identifier: target.Identifier,
            },
            reason: reason.trim() || null,
          },
        })
      } catch {
        // Toast already raised via errorToast on muteError; keep
        // going so a single bad target doesn't strand the rest.
      }
      done++
      setProgress({ done, total: targets.length })
    }

    setIsDialogOpen(false)
    onComplete?.()
  }

  async function runUnmuteBatch() {
    const targets = selectedRows.filter((t) => t.IsMuted)
    for (const target of targets) {
      try {
        await clearMute({
          variables: {
            input: {
              source: target.Source,
              identifier: target.Identifier,
            },
          },
        })
      } catch {
        // toast handled
      }
    }
    onComplete?.()
  }

  // Sample row used by the dialog header for "if you mute these,
  // here's what they look like" context. Picking the first selected
  // target is enough -- the dialog also lists the per-source counts.
  const sampleRow = selectedRows[0]

  return (
    <>
      {/*
        Mute action. Disabled when every selected row is already
        muted (in which case "Unmute" is the only sensible verb).
      */}
      {!allMuted && (
        <TooltipButton
          variant="ghost"
          isStacked
          iconSrc="bell-slash"
          content={
            isMixed
              ? "Mute every active target in the selection"
              : "Silence the selected targets"
          }
          onClick={() => setIsDialogOpen(true)}
        >
          Mute
        </TooltipButton>
      )}

      {/*
        Unmute action. Disabled when no selected row is muted, since
        there's nothing to clear. Mixed selections allow it -- the
        handler filters down to muted rows before firing.
      */}
      {!allActive && (
        <TooltipButton
          variant="ghost"
          isStacked
          iconSrc="bell"
          content={
            isMixed
              ? "Resume alerts for the muted targets in the selection"
              : "Resume alerts for the selected targets"
          }
          isLoading={clearing}
          onClick={runUnmuteBatch}
        >
          Unmute
        </TooltipButton>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <Dialog.Content align="top">
          <Dialog.Header>
            <Dialog.Title>
              Mute {selectedRows.length} target
              {selectedRows.length === 1 ? "" : "s"}
            </Dialog.Title>
            {sampleRow && (
              <Dialog.Description>
                {summariseSelection(selectedRows, sampleRow)}
              </Dialog.Description>
            )}
          </Dialog.Header>

          <ErrorMessage className="my-3" error={muteError} />

          <form
            id="uptime-bulk-mute-form"
            className="space-y-4 py-2"
            onSubmit={(e) => {
              e.preventDefault()
              runMuteBatch()
            }}
          >
            <p className="text-sm text-muted-foreground">
              Muted targets are excluded from KPIs and the Active incidents
              view. They still get probed and continue to record history, so you
              can audit the underlying availability later.
            </p>

            <div className="space-y-2">
              <Label htmlFor="uptime-bulk-mute-reason">
                Reason (applied to every selected target)
              </Label>
              <Textarea
                id="uptime-bulk-mute-reason"
                autoFocus
                rows={3}
                placeholder="e.g. scheduled maintenance until Tuesday"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            {isMixed && (
              <p className="text-xs text-muted-foreground">
                {selectedRows.filter((t) => t.IsMuted).length} of these are
                already muted; their reason will be replaced.
              </p>
            )}

            {/*
              Progress feedback while the per-target mutations run --
              gives the operator a "still going" signal when a batch
              of 50+ rows takes a moment to settle.
            */}
            {isMuting && (
              <p className="text-xs text-muted-foreground">
                Muting <span className="tabular-nums">{progress.done}</span> /{" "}
                <span className="tabular-nums">{progress.total}</span>…
              </p>
            )}

            {selectedRows.some((t) => t.IsMuted && t.MutedAt) && (
              <p className="text-xs text-muted-foreground">
                Most recent existing mute:{" "}
                {formatRelativeTime(latestMutedAt(selectedRows))}.
              </p>
            )}
          </form>

          <Dialog.Footer>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isMuting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isMuting}
              form="uptime-bulk-mute-form"
            >
              Mute {selectedRows.length}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  )
}

function countSources(rows: UptimeTargetFragment[]): number {
  const set = new Set<string>()
  for (const r of rows) set.add(r.Source)
  return set.size
}

// Builds the dialog subtitle. Single-target selections show the row's
// own name+category; multi-target selections summarise the count and
// the number of distinct categories they span.
function summariseSelection(
  rows: UptimeTargetFragment[],
  sample: UptimeTargetFragment,
): string {
  if (rows.length === 1) {
    return `${sample.Name ?? sample.Identifier} · ${getCategoryLabel(sample.Source)}`
  }
  const sources = countSources(rows)
  const noun = sources === 1 ? "category" : "categories"
  return `${rows.length} targets across ${sources} ${noun}`
}

function latestMutedAt(rows: UptimeTargetFragment[]): string {
  let latest = ""
  for (const r of rows) {
    if (r.MutedAt && r.MutedAt > latest) latest = r.MutedAt
  }
  return latest
}

export default UptimeBulkMuteAction
